import Drawer from "react-modern-drawer";
import {
  Cancel01Icon,
  Search01Icon,
  Time04Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { BaseInput, InputSize } from "shared/components/forms/BaseInput";
import { Loader } from "shared/components/Loader";
import { Pagination } from "shared/components/Pagination";
import { TimeLine } from "shared/components/TimeLine";
import { useMedia } from "shared/providers";
import { getErrorMessage } from "utils/error";
import { ActorFilterTabs } from "./components/ActorFilterTabs";
import { ActivityCard } from "./components/ActivityCard";
import { usePropertyActivityLogController } from "./usePropertyActivityLogController";
import { ActivityCard as ActivityCardType } from "./types";

export const PropertyActivityLog = ({
  showActivityLog,
  setShowActivityLog,
  propertyId,
  unitId
}: {
  showActivityLog: boolean;
  setShowActivityLog: (value: boolean) => void;
  propertyId?: string;
  unitId?: number;
}) => {
  const {
    activityLogMeta,
    cards,
    pagination,
    actorFilter,
    setActorFilter,
    page,
    setPage,
    searchQuery,
    setSearchQuery
  } = usePropertyActivityLogController({
    propertyId,
    unitId,
    enabled: showActivityLog
  });

  const { isMobile } = useMedia();

  const totalPages =
    pagination && pagination.per_page > 0
      ? Math.ceil(pagination.total / pagination.per_page)
      : 1;

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      onClose={() => setShowActivityLog(false)}
      open={showActivityLog}
      size={isMobile ? 380 : 640}
    >
      <div className="flex h-screen flex-col">
        <div className="flex items-center justify-between border-b border-alpha-80 px-6 py-4">
          <p className="text-xl font-bold text-content-1000">Activity log</p>
          <IconButton
            onClick={() => setShowActivityLog(false)}
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            HugeIcon={Cancel01Icon}
            className="stroke-content-1000"
          />
        </div>

        <div className="space-y-4 border-b border-alpha-40 px-6 pt-6">
          <BaseInput
            id="property-activity-log-search"
            size={InputSize.SM}
            placeholder="Search"
            Icon={Search01Icon}
            value={searchQuery}
            onChange={setSearchQuery}
          />

          <Show if={unitId == null}>
            <ActorFilterTabs filter={actorFilter} onChange={setActorFilter} />
          </Show>
        </div>

        <div className="size-full flex-1 overflow-x-hidden overflow-y-auto px-6 pb-6">
          <ActivityBody
            isLoading={activityLogMeta.isLoading}
            isError={activityLogMeta.isError}
            errorMessage={getErrorMessage(activityLogMeta.error)}
            cards={cards}
            isSearching={searchQuery.trim().length > 0}
          />

          <Show if={totalPages > 1}>
            <div className="mt-6">
              <Pagination
                total={totalPages}
                current={page}
                onChange={setPage}
                disabled={activityLogMeta.isFetching}
              />
            </div>
          </Show>
        </div>
      </div>
    </Drawer>
  );
};

const ActivityBody = ({
  isLoading,
  isError,
  errorMessage,
  cards,
  isSearching
}: {
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  cards: ActivityCardType[];
  isSearching: boolean;
}) => {
  if (isLoading)
    return (
      <div className="flex items-center justify-center py-10">
        <Loader />
      </div>
    );

  if (isError)
    return (
      <p className="py-10 text-center text-sm text-error-500">{errorMessage}</p>
    );

  if (!cards.length && isSearching)
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <div className="mb-6 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-alpha-40">
          <HugeiconsIcon
            icon={Search01Icon}
            size={40}
            strokeWidth={1.5}
            className="stroke-content-1000"
          />
        </div>
        <div className="w-full max-w-[314px] space-y-2">
          <p className="text-base font-bold text-content-500">
            No results found
          </p>
          <p className="text-sm font-medium text-content-300">
            Try a different search term.
          </p>
        </div>
      </div>
    );

  if (!cards.length)
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <div className="mb-6 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-alpha-40">
          <HugeiconsIcon
            icon={Time04Icon}
            size={40}
            strokeWidth={1.5}
            className="stroke-content-1000"
          />
        </div>
        <div className="w-full max-w-[314px] space-y-2">
          <p className="text-base font-bold text-content-500">
            No activity yet
          </p>
          <p className="text-sm font-medium text-content-300">
            Property updates will show up here, whether made manually or synced
            from your PMS.
          </p>
        </div>
      </div>
    );

  return (
    <TimeLine wrapperClassName="w-full" leftSlotWidth="128px">
      {cards.map((card) => (
        <ActivityCard key={card.id} card={card} />
      ))}
    </TimeLine>
  );
};
